import { type RefObject, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

// How far (px) the viewer scrolls before the header surface is fully painted. The ramp is linear
// from the top of the scrollport, so the header dissolves in as the cover scrolls out.
const HEADER_SURFACE_FADE_END_SCROLL_TOP = 200;

// The marker the fade resolves the header's scrollport by (see resolveHeaderScrollport). Spread
// SHOWCASE_SCROLLPORT_PROPS on the scroll root of every surface that renders this header over its
// own scroll area — the attribute is this hook's contract, deliberately NOT a selection or layout
// id, so it marks nothing but showcase scrollports.
const SHOWCASE_SCROLLPORT_ATTRIBUTE = 'data-showcase-scrollport';
export const SHOWCASE_SCROLLPORT_PROPS = { [SHOWCASE_SCROLLPORT_ATTRIBUTE]: '' };

/**
 * The scroll root the header at `headerElement` is pulled up over, resolved NEAREST-FIRST: walk up
 * from the header and take the scroll area inside the first ancestor that contains one at all.
 *
 * The walk keeps the fade bound to the header's OWN scrollport when more than one marked
 * scrollport shares the document: a document-global query returns the first match in DOCUMENT
 * ORDER, which is the OUTER one whenever a showcase surface renders nested inside another marked
 * scroller (the shape the showcase editor's shell-nesting had when the marker was the shared
 * `main` selection id — an outer scroller that never moves, leaving the header permanently
 * transparent). Same reason MouseSelectionProvider resolves nearest-first; the header can't use
 * it because it mounts ABOVE the page's scroll providers (see PublicShowcasePage).
 *
 * Bottom-up is also what keeps the ambiguity out: an ancestor high enough to contain BOTH scroll
 * areas would hand back the outer one, so the walk stops at the FIRST ancestor containing any
 * match — the showcase page's root element, which contains only the page's own.
 */
const resolveHeaderScrollport = (headerElement: HTMLElement | null) => {
  for (let ancestor = headerElement; ancestor; ancestor = ancestor.parentElement) {
    const scrollport = ancestor.querySelector<HTMLElement>(`[${SHOWCASE_SCROLLPORT_ATTRIBUTE}]`);

    if (scrollport) {
      return scrollport;
    }
  }

  return null;
};

export type UsePublicShowcaseHeaderScrollOpacityParams = {
  /**
   * Whether the header is overlaying an immersive cover. `false` keeps the surface fully painted (a
   * header in normal flow never fades), so this is the immersive-cover reading, not a feature flag.
   */
  enabled: boolean;
  /**
   * The header's own root element — the position the scroll root is resolved FROM, so the fade
   * tracks the scroller the header is actually pulled up over (see resolveHeaderScrollport).
   */
  headerRef: RefObject<HTMLElement | null>;
};

/**
 * The opacity the public collection header should paint its own surface at: `0` at the top of an
 * immersive cover (the cover shows through) ramping to `1` once the viewer has scrolled
 * HEADER_SURFACE_FADE_END_SCROLL_TOP past it, and a constant `1` whenever the header is not
 * overlaying a cover.
 *
 * Reads the scroll position off the page's scroll root through the DOM rather than through the
 * scroll element context: the header mounts ABOVE the page's scroll providers (it overlays the
 * scroll area it is pulled up behind — see PublicShowcasePage), so it cannot consume them. The
 * root is the showcase page's `<main>`, marked with SHOWCASE_SCROLLPORT_PROPS, and
 * `window.scrollY` backs up surfaces that scroll the document instead.
 */
export const usePublicShowcaseHeaderScrollOpacity = ({
  enabled,
  headerRef,
}: UsePublicShowcaseHeaderScrollOpacityParams) => {
  // Immersive starts transparent and non-immersive starts painted, so the FIRST paint is already
  // correct — a shared initial value would flash the wrong surface for one frame on one of them.
  const [headerSurfaceOpacity, setHeaderSurfaceOpacity] = useState(enabled ? 0 : 1);

  // Layout effect: the ramp is read from the DOM and applied to a painted surface, so a viewer
  // landing mid-scroll (hydration, a back navigation) must never paint a transparent header first.
  useIsomorphicLayoutEffect(() => {
    if (!enabled) {
      setHeaderSurfaceOpacity(1);

      return;
    }

    let detach: (() => void) | undefined;

    const attach = () => {
      detach?.();

      const scrollport = resolveHeaderScrollport(headerRef.current);
      const scrollTarget: HTMLElement | Window = scrollport ?? window;

      const handleScroll = () =>
        setHeaderSurfaceOpacity(
          Math.min((scrollport?.scrollTop ?? window.scrollY) / HEADER_SURFACE_FADE_END_SCROLL_TOP, 1),
        );

      // Sync the surface to wherever the scrollport already stands before listening for moves.
      handleScroll();
      scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
      detach = () => scrollTarget.removeEventListener('scroll', handleScroll);
    };

    attach();

    // The header renders before the page's scroll root exists, so the first attach can land on the
    // window fallback. Re-bind once after layout settles, when the real scrollport is mounted.
    const frameId = requestAnimationFrame(attach);

    return () => {
      cancelAnimationFrame(frameId);
      detach?.();
    };
  }, [enabled, headerRef]);

  return { headerSurfaceOpacity };
};
